(function (global) {
  'use strict';
  function createCheckoutController(options) {
    let inProgress = false; let timer;
    function syncButtons() {
      const pending = !!options.isPending?.(); const empty = !!options.isEmpty?.();
      for (const button of options.getButtons()) {
        button.disabled = inProgress || (empty && !pending);
        button.classList.toggle('disabled', button.disabled);
        button.setAttribute('aria-busy', String(inProgress || pending));
        button.setAttribute('aria-disabled', String(button.disabled));
      }
    }
    function reset() {
      clearTimeout(timer); inProgress = false;
      for (const button of options.getButtons()) {
        button.style.cursor = '';
        const loader = button.querySelector('.addloadr'); if (loader) loader.style.display = 'none';
      }
      syncButtons();
    }
    async function handoff(button) {
      if (inProgress) return;
      inProgress = true; syncButtons(); options.status?.('Preparing checkout…');
      try {
        await Promise.all([options.load(), options.whenIdle?.()]); await options.validateGifts?.(); await options.whenIdle?.();
        if (options.isEmpty?.()) { reset(); options.status?.('Add an item to your bag before checkout.'); return; }
        options.checkout(button);
        timer = setTimeout(() => {
          if (!options.isVisible?.()) { reset(); options.status?.('Checkout did not open. Please try again.', true); }
        }, 8000);
      } catch (error) { reset(); options.status?.('Checkout could not load. Please try again.', true); throw error; }
    }
    return { syncButtons, reset, handoff, get inProgress() { return inProgress; } };
  }
  if (typeof module !== 'undefined' && module.exports) { module.exports = { createCheckoutController }; return; }
  const doc = global.document;
  const configElement = doc.getElementById('bodycare-gokwik-config');
  if (!configElement || global.BodycareGoKwik) return;
  const config = JSON.parse(configElement.textContent);
  let loadPromise; let countryPromise; let ready = false; let analyticsStarted = false; let boundSdk;
  const readyWaiters = [];
  const sdkReady = () => ready || (typeof global.gokwikSdk?.triggerLoadingScreen === 'function' && typeof global.gokwikSdk?.initCheckout === 'function');
  function bindSdk() {
    const sdk = global.gokwikSdk;
    if (boundSdk === sdk || typeof sdk?.on !== 'function') return;
    boundSdk = sdk; sdk.on('modal_closed', () => controller.reset());
  }
  function onReady() {
    ready = true; bindSdk(); readyWaiters.splice(0).forEach(resolve => resolve()); controller.syncButtons();
    doc.querySelectorAll('#gokwik-buy-now').forEach(button => { button.disabled = false; button.classList.remove('disabled'); });
  }
  function configureGa() {
    if (analyticsStarted || !config.ga4 || config.ga4 === 'undefined') return;
    analyticsStarted = true; global.dataLayer = global.dataLayer || [];
    global.gtag = global.gtag || function () { global.dataLayer.push(arguments); };
    global.gtag('js', new Date()); global.gtag('config', config.ga4, { groups: 'GA4' });
    global.BodycareAssets.loadScript('https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.ga4), { defer: true }).catch(() => {});
  }
  async function configureCountry() {
    if (!config.enableIntlFlow) return;
    try {
      const response = await fetch((global.Shopify?.routes?.root || '/') + 'browsing_context_suggestions.json');
      if (!response.ok) return;
      const country = (await response.json())?.detected_values?.country_name;
      let style = doc.querySelector('[data-bodycare-gokwik-country]');
      if (!style) { style = doc.createElement('style'); style.dataset.bodycareGokwikCountry = ''; doc.head.appendChild(style); }
      style.textContent = country === 'India' ? '.shopifycheckoutbtn{display:none!important}' : '.gokwik-checkout,#gokwik-buy-now{display:none!important}.shopifycheckoutbtn{display:block!important}';
    } catch { /* Keep existing presentation if country discovery is unavailable. */ }
  }
  function load() {
    if (sdkReady() && typeof global.onCheckoutClick === 'function') { bindSdk(); return Promise.resolve(); }
    if (loadPromise) return loadPromise;
    configureGa(); countryPromise = countryPromise || configureCountry();
    let watchdog;
    const initialize = async () => {
      await global.BodycareAssets.loadScript(config.scriptUrl, { defer: true });
      if (!sdkReady()) await new Promise(resolve => readyWaiters.push(resolve));
      await countryPromise;
      if (typeof global.onCheckoutClick !== 'function') throw new Error('Checkout handler is unavailable.');
      bindSdk(); controller.syncButtons();
    };
    loadPromise = Promise.race([initialize(), new Promise((_, reject) => { watchdog = setTimeout(() => reject(new Error('Checkout initialization timed out.')), 8000); })])
      .catch(error => { loadPromise = null; throw error; }).finally(() => clearTimeout(watchdog));
    return loadPromise;
  }
  const controller = createCheckoutController({
    getButtons: () => doc.querySelectorAll('.gokwik-checkout button'),
    isReady: sdkReady, isPending: () => !!global.BodycareCart?.pending,
    isEmpty: () => global.BodycareCart?.cart ? !global.BodycareCart.cart.item_count : false,
    load, whenIdle: async () => {
      if (global.BodycareCart?.requiresReconciliation) await global.BodycareCart.reconcile();
      await global.BodycareCart?.whenIdle();
    }, validateGifts: () => global.BodycareCart?.ensureGiftsValid?.(),
    checkout: button => global.onCheckoutClick(button),
    status: (message, error) => global.BodycareCart?.status(message, error),
    isVisible: () => [...doc.querySelectorAll('iframe')].some(frame => /gokwik/.test(frame.src) && frame.getBoundingClientRect().height > 100 && getComputedStyle(frame).visibility !== 'hidden' && getComputedStyle(frame).display !== 'none')
  });
  global.BodycareGoKwik = { load, syncButtons: controller.syncButtons, get ready() { return sdkReady(); } };
  global.addEventListener('gokwikLoaded', onReady);
  ['bodycare:cart-content:rendered', 'bodycare:cart-pending', 'on:cart:add', 'on:line-item:change', 'bodycare:content:loaded'].forEach(name => doc.addEventListener(name, controller.syncButtons));
  doc.addEventListener('click', event => {
    const button = event.target.closest?.('.gokwik-checkout button');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation(); controller.handoff(button).catch(() => {});
  }, true);
  const start = () => {
    controller.syncButtons();
    if (config.immediate) { load().catch(() => {}); return; }
    const intent = event => {
      if (event.target.closest?.('#cart-icon,[href*="/cart"],[name="add"],.js-add-to-cart,.gokwik-checkout,#gokwik-buy-now')) load().catch(() => {});
    };
    ['pointerover', 'focusin', 'touchstart'].forEach(name => doc.addEventListener(name, intent, { capture: true, passive: true }));
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})(typeof window === 'undefined' ? globalThis : window);
